import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Calendar } from 'lucide-react';

const CalendarTimePicker = ({ controls = ['calendar', 'time'], onClose, onConfirm }) => {
  const today = new Date();
  const currentTime = today.toTimeString().split(" ")[0];
  const defaultDate = new Date();
  defaultDate.setDate(today.getDate() + 5);

  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [selectedTime, setSelectedTime] = useState(currentTime);
  const [currentMonth, setCurrentMonth] = useState(defaultDate);
  const showCalendar = controls.includes('calendar');
  const showTime = controls.includes('time');
  const [muhuratInfo, setMuhuratInfo] = useState('');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };
  const handleDateClick = (date) => {
    if (!isPastDate(date)) {
      setSelectedDate(date);
    }
  };
  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    // Only allow navigation if the previous month is not before the current month
    if (prevMonth.getMonth() >= today.getMonth() && prevMonth.getFullYear() >= today.getFullYear()) {
      setCurrentMonth(prevMonth);
    }
  };
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  const canNavigatePrev = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    return prevMonth.getMonth() >= today.getMonth() && prevMonth.getFullYear() >= today.getFullYear();
  };
  const handleTimeChange = (time) => {
    setSelectedTime(time);
    // setIsTimeDropdownOpen(false);
  };

  const handleConfirm = () => {
    const finalDate = selectedDate || defaultDate; // use selected date OR fallback
    onConfirm?.({ date: finalDate, time: selectedTime, muhuratInfo });
  };

  const isToday = (date) => {
    const today = new Date();
    return date &&
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date) => {
    return date &&
      selectedDate &&
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
  };
  const isPastDate = (date) => {
    if (!date) return false;
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0); // Reset to start of today
    tomorrow.setDate(tomorrow.getDate() + 5); // Move to tomorrow
    return date < tomorrow;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className=' bg-black/60 w-full z-30 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center h-full'>
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="flex justify-end p-1">
        <button type='button' className='text-red-600 py-1 px-2 font-semibold border border-red-600 rounded' onClick={onClose}>X</button>
        </div>
        <div className='grid grid-cols-2 border-t border-gray-200'>
          <div>
            <div className="mb-3">
              <div className="flex items-center p-4 gap-2 mb-2 border-b border-gray-200">
                <Calendar className="w-5 h-5 text-blue-600" />
                <h2 className="text-sm font-semibold text-gray-800">Select Date & Time</h2>
              </div>
            </div>
            {showCalendar && (
              <div className="mb-3 px-4">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-2">
                  {canNavigatePrev() ? (
                    <button
                      onClick={handlePrevMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                  ) : (
                    <div className="w-9 h-9"></div>
                  )}
                  <h3 className="text-sm font-semibold text-gray-800">
                    {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h3>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                {/* Days of Week Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="text-center text-[13px] font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((date, index) => (
                    <button
                      key={index}
                      onClick={() => date && handleDateClick(date)}
                      disabled={!date || isPastDate(date)}
                      className={`
                  h-10 w-10 rounded-lg text-[13px] font-medium transition-all
                  ${!date ? 'invisible' : ''}
                  ${isPastDate(date)
                          ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                          : isSelected(date)
                            ? 'bg-red-600 text-white shadow-md'
                            : isToday(date)
                              ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              : 'text-gray-700 hover:bg-gray-100'
                        }
                  ${date && !isSelected(date) && !isPastDate(date) ? 'hover:scale-105' : ''}
                `}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className='bg-gray-50'>
            {showTime && (
              <div className="space-y-3">
                <div className="flex items-center p-4 gap-2 mb-2 border-b border-gray-300">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-800 m-0">Select Time</label>
                </div>
                <div className="relative px-4">
                  <div className='mt-2'>
                    <label className="text-sm font-semibold text-gray-800">Time</label>
                    <input type='time' step="1" value={selectedTime}
                      onChange={(e) => handleTimeChange(e.target.value)} placeholder='' className="border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full text-sm" />
                  </div>
                  <div className='mt-2'>
                    <label className="text-sm font-semibold text-gray-800">Additional info on muhurat if any</label>
                    <input type='text' value={muhuratInfo}
                      onChange={(e) => setMuhuratInfo(e.target.value)} placeholder='Example: 8 - 10AM' className="border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full text-sm" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-gray-200">
          <button type='button' onClick={handleConfirm} className="w-full bg-red-600 text-sm text-white py-2.5 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium">
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
};
export default CalendarTimePicker;






